import { useState } from "react";
import { Star, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ReviewForm({ sessionId, productName, productId }) {
    const [open, setOpen] = useState(true);
    const [rating, setRating] = useState(5);
    const [hover, setHover] = useState(0);
    const [name, setName] = useState("");
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name || !title || !body) return;
        setSubmitting(true);
        try {
            await api.post("/reviews", {
                session_id: sessionId,
                product_id: productId || undefined,
                name,
                email: email || undefined,
                rating,
                title,
                body,
            });
            setSubmitted(true);
            toast.success("Thanks — your review is live.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit review.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <section
            data-testid="review-form-section"
            className="mt-16 border border-white/15 p-8 lg:p-10 bg-black/40 backdrop-blur-sm relative max-w-2xl mx-auto text-left"
        >
            <button
                onClick={() => setOpen(false)}
                aria-label="Dismiss"
                data-testid="review-dismiss"
                className="absolute top-4 right-4 p-1 text-muted-foreground hover:text-white"
            >
                <X size={16} />
            </button>

            {submitted ? (
                <div className="text-center py-6" data-testid="review-thank-you">
                    <Check size={28} className="mx-auto text-white mb-4" />
                    <p className="label-mono">— Review Published</p>
                    <p className="font-display text-3xl uppercase mt-3">
                        Thank you.
                    </p>
                    <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">
                        Your words show up on the next visitor's screen — that's how the brand grows.
                    </p>
                </div>
            ) : (
                <>
                    <p className="label-mono mb-2">— One last thing</p>
                    <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-tight">
                        How was{" "}
                        <span className="italic">
                            {productName || "your piece"}?
                        </span>
                    </h2>
                    <p className="text-muted-foreground text-sm mt-2">
                        Help future buyers — leave a quick review.
                    </p>

                    <form onSubmit={submit} className="mt-8 space-y-6" data-testid="review-form">
                        {/* Star rating */}
                        <div>
                            <p className="label-mono mb-2">Rating</p>
                            <div className="flex items-center gap-2" role="radiogroup">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setRating(n)}
                                        onMouseEnter={() => setHover(n)}
                                        onMouseLeave={() => setHover(0)}
                                        aria-label={`${n} stars`}
                                        data-testid={`review-rating-${n}`}
                                        className="p-1"
                                    >
                                        <Star
                                            size={28}
                                            fill={n <= (hover || rating) ? "currentColor" : "transparent"}
                                            className={n <= (hover || rating) ? "text-white" : "text-white/30"}
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="label-mono block mb-2">Headline</label>
                            <input
                                required
                                maxLength={80}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Built different."
                                data-testid="review-title"
                                className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-display text-xl"
                            />
                        </div>

                        <div>
                            <label className="label-mono block mb-2">Review</label>
                            <textarea
                                required
                                maxLength={600}
                                rows={4}
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder="Tell us how it fits, how it feels, what surprised you…"
                                data-testid="review-body"
                                className="w-full bg-transparent border border-white/15 focus:border-white p-3 outline-none font-sans text-sm resize-none"
                            />
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1 text-right">
                                {body.length} / 600
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="label-mono block mb-2">Your name</label>
                                <input
                                    required
                                    maxLength={60}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="First name + last initial"
                                    data-testid="review-name"
                                    className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-sans"
                                />
                            </div>
                            <div>
                                <label className="label-mono block mb-2">Email (optional)</label>
                                <input
                                    type="email"
                                    maxLength={120}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="never shown publicly"
                                    data-testid="review-email"
                                    className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-sans"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="review-submit"
                            className="w-full bg-white text-black py-4 font-mono text-xs uppercase tracking-[0.3em] hover:bg-white/85 disabled:opacity-60"
                        >
                            {submitting ? "Submitting…" : "Publish Review →"}
                        </button>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center">
                            Tagged as verified buyer
                        </p>
                    </form>
                </>
            )}
        </section>
    );
}
